<template>
  <FormKit
    :id="formkitId"
    v-bind="bindings"
    ref="phoneNumberEl"
    v-model="phoneNumber"
    :value="phoneNumber"
    inputClass="input-remove-box-shadow"
    :suffixIcon="showClearIcon === true && phoneNumber && phoneNumber.length > 0 ? CLEAR_ICON : ''"
    suffixIconClass="cursor-pointer h-fit !self-auto pr-3"
    @suffixIconClick="clearField()"
    @input="onChange(phoneNumberEl)"
    @blur="onBlur(phoneNumberEl)"
  />
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { FormKit } from '@formkit/vue';
import { createMessage } from '@formkit/core';
import { CLEAR_ICON } from '@/constants/svg-images.const';
import { PHONE_AUS_COUNTRY_CODE } from '@/constants/general-words.const';
import { getNode } from '@formkit/core';

const formkitId = 'phone-input-' + Math.random().toString(36).slice(2);
const nodeRef = ref(null);
const node = ref(null);

defineExpose({
  node,
});

onMounted(() => {
  node.value = getNode(formkitId);
});

const props = defineProps<{
  bindings: any;
}>();

const emit = defineEmits(['update:modelValue']);
const phoneNumberEl = ref<any>();
const phoneNumber = defineModel<string>('');

const showClearIcon = computed(() => props.bindings.showClearIcon as boolean);

const finalRegex = /^(0[1-9]\)|0[1-9])?( ?-?[0-9]){7,9}$|(^13\d{4}$)/;
const maxValidLength = 10;

function clearField() {
  phoneNumber.value = '';
  emit('update:modelValue', phoneNumber.value);
}

//onChange has been added to prevent duplicate validation messages, if it's empty remove any custom validation.
function onChange(e: any) {
  let value = e?.node?.value;

  //There is a quirk where if the value length is 9 and there is
  //a custom validation message there will be a duplicate validation message.
  if (!value || phoneNumberEl.value.node.store?.rule_required || phoneNumberEl.value.node.store?.rule_matches) {
    phoneNumberEl.value.node.store.filter(() => false, 'custom');
    return;
  }
}

function onBlur(e: any) {
  let value = e?.node?.value;

  if (!value) {
    phoneNumberEl.value.node.store.filter(() => false, 'custom');
    emit('update:modelValue', phoneNumber.value);
    return;
  }

  const startsWith13 = value.substring(0, 2) === '13';

  //Handle if there  is a space in a 13 number.
  if (startsWith13 && value.length > 6) {
    value = value.replace(/[^0-9]/g, '');
  }

  //Don't sanitise the number if it hasn't passed the input validation.
  const basicInputIsValid = new RegExp(props.bindings.validationRegex).test(value);
  if (!basicInputIsValid) {
    emit('update:modelValue', value);
    return;
  }

  //Remove all non numberic values.
  value = value.replace(/[^0-9]/g, '');

  //if number starts with 61 remove it.
  let firstTwoChars = value.substring(0, 2);

  if (firstTwoChars === PHONE_AUS_COUNTRY_CODE) {
    value = value.substring(2, value.length);
  }

  //If not 13 number and phone number doesn't start with zero prepend zero.
  if (!startsWith13) {
    const firstChar = value.substring(0, 1);
    if (firstChar !== '0') {
      value = `0${value}`;
    }
  }

  value = value.slice(0, maxValidLength);

  if (phoneNumberEl.value) {
    phoneNumberEl.value.node.input(value);
  }

  //This should never really happen but if it's still somehow invalid.
  if (!new RegExp(finalRegex).test(value) || value.length > maxValidLength) {
    phoneNumberEl.value.node.store.set(
      createMessage({
        key: 'custom',
        type: 'custom',
        value: props.bindings.validationMessages.matches,
        blocking: true,
      })
    );
  } else {
    //Hide validation if it's been triggered before.
    if (phoneNumberEl.value.node.store.custom) {
      phoneNumberEl.value.node.store.filter(() => false, 'custom');
    }
  }

  emit('update:modelValue', phoneNumber.value);
}

watch(
  () => phoneNumber.value,
  (newVal) => {
    if (newVal) {
      emit('update:modelValue', newVal);
    }
  }
);
</script>
